import { useCallback, useEffect, useState } from "react";

import { type AegFile, type AegOccurrenceId, downloadAegFile, fetchAegFiles, removeAegFile, uploadAegFile } from "../api/aegOccurrences";

export type AegFilesError = { id: string; values?: Record<string, string> };

export const useAegOccurrenceFiles = (aegOccurrenceId: AegOccurrenceId) => {
  const [files, setFiles] = useState<AegFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<AegFilesError | undefined>(undefined);
  const [fileToDelete, setFileToDelete] = useState<AegFile | null>(null);

  const loadFiles = useCallback(async () => {
    if (!aegOccurrenceId) {
      setFiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setFiles(await fetchAegFiles(aegOccurrenceId));
    } finally {
      setLoading(false);
    }
  }, [aegOccurrenceId]);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  const uploadFiles = async (picked: File[]) => {
    if (!aegOccurrenceId || picked.length === 0) return;

    const maxSize = 20 * 1024 * 1024;
    const tooLarge = picked.find((file) => file.size > maxSize);
    if (tooLarge) {
      setError({ id: "intl-msg:support.aeg.detail.attachments.error.tooLarge", values: { name: tooLarge.name } });
      return;
    }

    setUploading(true);
    try {
      const results = await Promise.all(picked.map((file) => uploadAegFile(aegOccurrenceId, file)));
      if (results.some((ok) => !ok)) {
        setError({ id: "intl-msg:support.aeg.detail.attachments.error.upload" });
      }
      await loadFiles();
    } finally {
      setUploading(false);
    }
  };

  const download = async (file: AegFile) => {
    const blob = await downloadAegFile(aegOccurrenceId, file.fileId);
    if (!blob) {
      setError({ id: "intl-msg:support.aeg.detail.attachments.error.download" });
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const removeFile = async () => {
    if (!fileToDelete) return;

    const removed = await removeAegFile(aegOccurrenceId, fileToDelete.fileId);
    if (removed) {
      setFiles((prev) => prev.filter((file) => file.fileId !== fileToDelete.fileId));
    } else {
      setError({ id: "intl-msg:support.aeg.detail.attachments.error.remove" });
    }
    setFileToDelete(null);
  };

  return {
    files,
    loading,
    uploading,
    error,
    fileToDelete,

    uploadFiles,
    download,
    removeFile,
    setFileToDelete,
    clearError: () => setError(undefined),
  };
};

export type AegOccurrenceFilesController = ReturnType<typeof useAegOccurrenceFiles>;
